import type { eventWithTime } from '@carboncopyco/rrweb-types';
import type { IDemo } from './Demo';
import type { EditToolColorValues, EditToolLayoutValues } from './EditTool';
import type { Folder } from './Folder';
import type { IntegrationMetadata, IntegrationVendor } from './Integrations';
import type { AudioMedia, ImageMedia, VideoMedia } from './Media';
import type {
  TourStepConfig,
  TourStepConfigUpdateRequest,
  TourStepTooltipConfig,
} from './TourStepTooltip';
import type { BasicUser, IUser } from './User';
import type { Workspace } from './Workspaces';

type MenuNames = 'changeDesign' | 'layout' | 'brightness';

export const TOUR_TOOLTIP_ELEMENT_ID = 'demostack-tour-tooltip';
export const TOUR_TOOLTIP_AREA_ELEMENT_ID = 'demostack-tour-tooltip-area';
export const TOUR_TOOLTIP_INNER_ELEMENT_ID = 'demostack-tour-inner-tooltip';
export const TOUR_TOOLTIP_ARROW_ID = 'demostack-tour-arrow';

// TODO: this is not a type
export const configsSpecificToTooltip = [
  'buttons.nextButton.label',
  'buttons.nextButton.action.name',
  'buttons.nextButton.action.url',
  'buttons.nextButton.action.goToStepId',
  'buttons.previousButton.label',
  'buttons.previousButton.action.name',
  'buttons.previousButton.action.url',
  'buttons.previousButton.action.goToStepId',
  'buttons.lastStepButton.label',
  'buttons.lastStepButton.action.name',
  'buttons.lastStepButton.action.url',
  'buttons.lastStepButton.action.goToStepId',
  'balloon.layout.alignment',
  'balloon.layout.relation',
  'navigationOptions.isButtonsNavigation',
  'navigationOptions.isElementNavigation',
  'navigationOptions.elementAction.name',
  'navigationOptions.elementAction.url',
  'navigationOptions.elementAction.goToStepId',
];

export enum TourStepEngineEditorMessageType {
  onEditStep = 'onEditStep',
  elementSelected = 'elementSelected',
  onStepEngineReady = 'onStepEngineReady',
  updateAllSteps = 'updateAllSteps',
  switchElementSelected = 'switchElementSelected',
  onStepConfigUpdate = 'onStepConfigUpdate',
  onTooltipDisplayed = 'onTooltipDisplayed',
  onBackStep = 'onBackStep',
  onNextStep = 'onNextStep',
  onRestartTour = 'onRestartTour',
  onDismissPositionWarning = 'onDismissPositionWarning',
  onMediaUpload = 'onMediaUpload',
  onOpenTourThemeCreateModal = 'onOpenTourThemeCreateModal',
  onAnalyticsEvent = 'onAnalyticsEvent',
}

export enum TourStepEnginePreviewMessageType {
  onBackStep = 'onBackStep',
  onNextStep = 'onNextStep',
  onRestartTour = 'onRestartTour',
  onStepEngineReady = 'onStepEngineReady',
  onCustomStep = 'onCustomStep',
  onTooltipDisplayed = 'onTooltipDisplayed',
  onExternalUrl = 'onExternalUrl',
  onTourEnd = 'onTourEnd',
}

export interface TourStepEnginePreviewAction {
  type: TourStepEnginePreviewMessageType;
  payload?: TourStep;
}
export interface TourStepEngineEditorAction {
  type: TourStepEngineEditorMessageType;
  payload?: {
    step?: TourStep;
    xpath?: TourStep['xpath'];
    stepConfigUpdateRequest?: TourStepConfigUpdateRequest;
    frameId?: TourFrame['id'];
    elementPosition?: TourStep['elementPosition'];
  };
}

export enum TourStepEngineIncomingMessageType {
  setInspectMode = 'setInspectMode',
  removeTourStep = 'removeTourStep',
  tourEnded = 'tourEnded',
  editTourStep = 'editTourStep',
  showTourStep = 'showTourStep',
  cancelInspectMode = 'cancelInspectMode',
  highlightElement = 'highlightElement',
  removeHighlight = 'removeHighlight',
  setIsApplyingConfigChanges = 'setIsApplyingConfigChanges',
  setHasStepConfigUpdates = 'setHasStepConfigUpdates',
  setTooltipScale = 'setTooltipScale',
  updateTooltipArrowOrientation = 'updateTooltipArrowOrientation',
  onMediaUpload = 'onMediaUpload',
  stopPlayingMedia = 'stopPlayingMedia',
}
export interface TourStepEngineIncomingAction {
  type: TourStepEngineIncomingMessageType;
  payload?: unknown;
}
export enum TourCaptureIncomingMessageType {
  startTour = 'startTour',
  continueTour = 'continueTour',
  endTour = 'endTour',
  restartTour = 'restartTour',
  pageGoBack = 'goBack',
  isTour = 'isTour',
  tourIsCapturing = 'tourIsCapturing',
  requestSnapshot = 'requestSnapshot',
  startInnerDocumentTour = 'startInnerDocumentTour',
  removeClickDisabledOverlay = 'removeClickDisabledOverlay',
}

export interface TourCapturePostMessageAction {
  type: TourCaptureIncomingMessageType;
  payload?: unknown;
}

export enum TourCaptureOutgoingMessageType {
  mimesisReplicateStatus = 'mimesisReplicateStatus',
  updatePageTitle = 'updatePageTitle',
  recordingInProgress = 'recording-in-progress',
  fileUpload = 'file-upload',
  URL_CHANGED = 'URL_CHANGED',
  DEMO_BACK = 'DEMO_BACK',
  PAGE_NOT_AVAILABLE = 'PAGE_NOT_AVAILABLE',
  BEFORE_UNLOAD = 'BEFORE_UNLOAD',
  iframeDetected = 'iframeDetected',
  isCapturingTour = 'isCapturingTour',
  blobToBase64ConversionResult = 'BLOB_TO_BASE64_CONVERSION_RESULT',
  mimesisReplicateLoaded = 'mimesisReplicateLoaded',
}

export interface TourCaptureOutgoingAction {
  type: TourCaptureOutgoingMessageType;
  payload?: unknown;
}

export declare type RRWebEvent = eventWithTime & {
  data: {
    height?: number | string;
    width?: number | string;
    x: number;
    y: number;
  };
  firstUrl: string;
  frameReset?: boolean;
  frameSeparator?: boolean;
  clickSeparator?: boolean;
  separatorIndex: number;
};

export declare type clickPosition = {
  x: number;
  y: number;
};

export interface TourUpdate {
  screenshot: string;
  revisionNumber: number;
  user: Omit<IUser, 'rolesToPermissions' | 'defaultOrganization'>;
  dateUpdated: string;
  revisionName: string;
}

export interface FrameLoading {
  frameIndex: number;
  isLoading: boolean;
  progress?: number;
}

export interface TourCreatorState {
  isCapturing: boolean;
  isReadyToCapture: boolean;
  tourId: Tour['id'] | null;
  tourFrames: TourFrame[];
  isCapturingFinished: boolean;
  selectedStepId: string | null;
  activeFrameId: number | null;
  addingStepInProgress: boolean;
  fileServiceToken: string | null;
  canFinishCapturing: boolean;
  isCaptureStarted: boolean;
  isCaptureButtonDisabled: boolean;
  framesLoading: FrameLoading[] | [];
  isCaptureSnapshotPending: boolean;
  isGeneratingReplayFrameChunks: boolean;
}

export interface ToursState {
  tours: Tour[];
}

type TourDemoData = {
  demoCreateTime: string;
  demoName: string;
  demoId: number;
  demoRecordingUuid: string;
  demoRevisionId: number;
  isDeleted: boolean;
  workspaceId: Workspace['id'];
};

type TourUrlData = {
  sourceTag: string;
  url: string;
};

export type WrapperType = 'browser' | 'device';

export type BackgroundType = 'color' | 'image';

export type Dimension = {
  width: number;
  height: number;
};

export type ScreenResolution = Dimension & {
  type: DeviceType;
};

export type DeviceType =
  | 'desktop'
  | 'tablet'
  | 'mobile'
  | 'custom'
  | 'responsive';

type Background = {
  wrapperType?: WrapperType;
  backgroundType?: BackgroundType;
  color?: EditToolColorValues;
  image?: string;
  blur?: boolean;
};

export type TourSettings = {
  showStepCounter: boolean;
  responsiveness: boolean;
  smallScreenWarning: boolean;
  screenResolution?: ScreenResolution;
  showTooltips: boolean;
  responsivenessDisabled: boolean;
  background?: Background;
  navigationToggle: boolean;
  tourThemeId?: number;
};

export type Tour = {
  archiveTime?: string;
  createTime: string;
  creator: BasicUser;
  demoData: TourDemoData[];
  demoId: number;
  demoRevisionId: number;
  description: string;
  id: number;
  isDeleted: boolean;
  revisionId: string | number;
  slugName: string;
  status: string;
  stepsCount: number;
  tourName: string;
  updateTime: string;
  urlData?: TourUrlData[];
  viewsCount: number;
  image: string;
  fileServiceToken: string;
  totalShares?: number;
  workspaceId: string;
  source: 'platform' | 'desktop';
  tourSettings: TourSettings;
  draftSettings: TourSettings;
  folderId?: Folder['id'];
  organizationId: string;
  readOnly?: boolean;
};

export type MediaContent = ImageMedia | VideoMedia | AudioMedia;

export interface TourStep {
  content: string;
  eventType: string;
  stepType: TourStepType;
  title: string;
  xpath: string;
  elementPosition?: {
    xTopLeft: number;
    yTopLeft: number;
    width: number;
    height: number;
  };
  stepId: string;
  showTooltip?: boolean;
  config?: TourStepConfig;
  mediaContent?: MediaContent[];
}
export interface TourFrame {
  archiveTime?: string;
  createTime: string;
  creator: BasicUser | string;
  frameActionLabel: string;
  frameDataUrl?: string;
  frameIndex: number;
  frameTitle: string;
  id: number;
  isActive: boolean;
  isDeleted: boolean;
  isDisabled: boolean;
  isDraft: boolean;
  frameType: FrameType;
  modified?: boolean;
  pageUrl: string;
  pageTitle: string;
  frameEventType: FrameEventType;
  steps: TourStep[];
  image: string;
  frameScreenshotUrl: string | null;
  tourData: {
    id: number;
    revisionId: number;
  };
  updateTime: string;
  captureIndex: number;
  copiedFrom?: {
    tourId: number;
    tourRevisionId: number;
  } | null;
  demoData: {
    demoId: number;
    demoRecordingUuid: string;
    demoRevisionId: number;
    demoTag: string;
  };
}

export interface TourEngineFrame extends TourFrame {
  frameActionLabel: string;
  frameData: RRWebEvent[];
  frameIndex: number;
  frameTitle: string;
  pageUrl: string;
  steps: TourStep[];
  tourId: number;
}

export enum FrameEventType {
  click = 'click',
  finish = 'finish',
}

export enum FrameType {
  screenshot = 'screenshot',
  recording = 'recording',
}

export interface TourShareDetails {
  creator: BasicUser;
  tour: Tour;
  frames: TourFrame[];
  options: {
    integrations: IntegrationVendor[];
  };
}

export type TourAnalytics = {
  creator?: {
    email?: string;
    name?: string;
  };
  durationInSeconds: number;
  ended: number;
  started: number;
  linkName: string;
  revisionId: number;
  shareId: string;
  stepsCompleted: number;
  stepsCount: number;
};

export interface TourCreator {
  demoId: IDemo['id'];
  isCapturing: boolean;
  isReadyToCapture: boolean;
  tourId: Tour['id'];
  tourFrames: TourFrame[];
  isCapturingFinished: boolean;
}

export interface TourPreviewStep {
  step: TourStep;
  frames: TourFrame[];
  lastFrames: TourFrame[];
}

export interface StepCount {
  current: number;
  total: number;
}

export interface ShowStepTooltipInput {
  step: TourStep;
  frameType: FrameType;
  stepCount?: StepCount;
  tourSettings?: Tour['tourSettings'];
  isTourFirstStep?: boolean;
  isTourLastStep?: boolean;
  layout?: EditToolLayoutValues;
  frames?: TourFrame[];
  documentRoot?: Document;
  tooltipScale?: number;
  fileServiceApiToken: string;
}

export interface CreateStepTooltipInput extends ShowStepTooltipInput {
  onStepTooltipChange: ({
    newStep,
    containingFrameId,
  }: {
    newStep: TourStep;
    containingFrameId: TourFrame['id'];
  }) => void;
  isStepEditable?: boolean;
  onUpdateAllSteps: () => void;
  onOpenTourThemeCreateModal: () => void;
  onStepConfigUpdate: (
    stepConfigUpdateRequest: TourStepConfigUpdateRequest
  ) => void;
  showMenu?: MenuNames;
  layout?: EditToolLayoutValues;
  frameId?: TourFrame['id'];
  documentRoot?: Document;
  isPositionWarningActive: boolean;
  onDismissPositionWarning: () => void;
  fileServiceApiToken: string;
  organizationId: string;
  onMediaUpload: (media: string, type: MediaContent['type']) => void;
  onAnalyticsEvent: (eventName: string) => void;
}

export enum TourStepType {
  tooltip = 'TOOLTIP',
  modal = 'MODAL',
}

export enum TourStepEventType {
  click = 'CLICK',
}

export enum TourStepAddDirection {
  above = 'ABOVE',
  below = 'BELOW',
}

export interface onAddStepProps {
  step: TourStep;
  referenceStepId: TourStep['stepId'] | null;
  direction: TourStepAddDirection | null;
}

export interface SetInspectElementInput {
  step?: TourStep;
  addStepOptions?: {
    referenceStepId?: TourStep['stepId'];
    direction?: TourStepAddDirection;
  };
  editTooltipMode?: boolean;
  frameType?: FrameType;
}

export interface IAddStepOptions {
  referenceStepId: TourStep['stepId'];
  direction: TourStepAddDirection;
}

export interface HighlightElement {
  step: TourStep;
}

export enum TourHighlightInspectAction {
  element = 'element',
}

export type ButtonState = 'previousButton' | 'nextButton' | 'lastStepButton';

export enum TooltipArrowPlacement {
  top = 'top',
  right = 'right',
  bottom = 'bottom',
  left = 'left',
}

export type TourIntegrationMetadata = {
  integrationId: number;
  opportunityId: string;
};

export type TourPresentation = {
  createTime: string;
  creator: {
    createTime: string;
    defaultOrganization: string;
    email: string;
    firstName: string;
    id: string;
    image: string;
    isDeleted: true;
    lastName: string;
    name: string;
    updateTime: string;
  };
  forClient: string;
  id: number;
  image: string;
  integrationMetadata: IntegrationMetadata;
  isDeleted: true;
  notes: string;
  revisionId: number;
  tourId: number;
  updateTime: string;
};

export type TourTheme = {
  name: string;
  category: 'Demostack default' | 'Published themes';
  tooltipConfig: TourStepTooltipConfig;
  id: number;
  isDeleted: boolean;
  createTime: string;
  updateTime: string;
  archiveTime: string;
  organizationId: string;
  creator: string;
};
